import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Recently viewed products row.
 *
 * Reads the recently-viewed product IDs from localStorage (populated on every PDP
 * by snippets/scripts.liquid), excludes the current product, then fetches its own
 * section through the search route with an `id:` query and injects the rendered
 * product cards into the list. The whole element stays `hidden` until there is at
 * least one card to show, so an empty heading is never rendered.
 *
 * Mirrors the IDs->cards mechanism used by predictive search (assets/predictive-search.js).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list - The list container the cards are injected into.
 * @property {HTMLElement} [heading] - The section heading.
 * @extends {Component<Refs>}
 */
class RecentlyViewedProducts extends Component {
  requiredRefs = ['list'];

  /** @type {number} The maximum number of cards to load (slider scrolls through them). */
  #maxProducts = 12;

  connectedCallback() {
    super.connectedCallback();

    requestIdleCallback(() => {
      this.#load();
    });
  }

  /**
   * The current product's ID, so it can be excluded from the list.
   * @returns {string | null}
   */
  get #currentProductId() {
    return this.dataset.currentProductId || null;
  }

  /**
   * The IDs of the products to display: recently viewed, minus the current product,
   * capped at the maximum.
   * @returns {string[]}
   */
  get #productIds() {
    const currentId = this.#currentProductId;

    return RecentlyViewed.getProducts()
      .map((/** @type {string} */ id) => `${id}`)
      .filter((/** @type {string} */ id) => id && id !== currentId)
      .slice(0, this.#maxProducts);
  }

  async #load() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const productIds = this.#productIds;
    if (productIds.length === 0) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    let markup;
    try {
      markup = await sectionRenderer.getSectionHTML(sectionId, false, url);
    } catch (error) {
      return;
    }

    if (!markup) return;

    const fetchedList = new DOMParser().parseFromString(markup, 'text/html').getElementById('recently-viewed-list');

    // No matched products (e.g. all unavailable / deleted) — stay hidden.
    if (!fetchedList || fetchedList.children.length === 0) return;

    this.refs.list.replaceChildren(...fetchedList.children);
    this.hidden = false;
    this.#setupCarousel();
  }

  /**
   * Wire the prev/next arrows to scroll the track one page at a time, and toggle
   * their visibility at the ends / when everything already fits.
   */
  #setupCarousel() {
    const { list, prev, next } = this.refs;
    if (!list) return;

    const scrollByPage = (/** @type {number} */ dir) => {
      list.scrollBy({ left: dir * list.clientWidth, behavior: 'smooth' });
    };

    if (prev instanceof HTMLElement) prev.addEventListener('click', () => scrollByPage(-1));
    if (next instanceof HTMLElement) next.addEventListener('click', () => scrollByPage(1));

    const update = () => this.#updateArrows();
    list.addEventListener('scroll', update, { passive: true });
    // Measure overflow reliably: ResizeObserver fires once the track is laid out
    // and again whenever the cards/viewport resize (single rAF can measure too early).
    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(list);
    } else {
      window.addEventListener('resize', update, { passive: true });
      requestAnimationFrame(update);
    }
    update();
  }

  #updateArrows() {
    const { list, prev, next } = this.refs;
    if (!list) return;

    // Centre the arrows on the card IMAGE (Figma), not the full slider height:
    // expose the image midline (relative to the positioned __inner) as a CSS
    // var. Recomputed here because the same ResizeObserver fires on layout and
    // on every card/viewport resize.
    const media = list.querySelector('.rg-product-card__media');
    if (media instanceof HTMLElement && media.offsetHeight > 0) {
      this.style.setProperty('--rv-arrow-top', `${list.offsetTop + media.offsetHeight / 2}px`);
    }

    const overflows = list.scrollWidth - list.clientWidth > 1;

    // PJ-4850: BOTH arrows stay visible whenever the track scrolls (the
    // Figma shows a pair; hiding the start/end arrow read as "missing").
    // Ends just no-op — scrollBy clamps natively.
    if (prev instanceof HTMLElement) prev.hidden = !overflows;
    if (next instanceof HTMLElement) next.hidden = !overflows;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
